import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Modal, Row, Select, Typography } from 'antd';
import { HabitatsMap } from '../map/HabitatsMap';
import { fetchHabitats } from '../../externalAPI/planetAPI';
import { Habitat } from '../../interfaces';
import habitatTerrestrialCommunityList from '../../assets/data/EUNIS_terrestrial_habitats_habitats_of_Community_interest.csv';
import habitatTerrestrialRedList from '../../assets/data/EUNIS_terrestrial_habitats_European_red_list.csv';
import habitatMarineRedList from '../../assets/data/EUNIS_marine_habitats_European_red_list.csv';
import habitatMarineCommunityList from '../../assets/data/EUNIS_marine_habitats_habitats_of_Community_interest.csv';
import habitatTerrestrialDetailList from '../../assets/data/EUNIS_terrestrial_habitats_description.json';
import habitatMarineDetailList from '../../assets/data/EUNIS_marine_habitats_description.json';

import Papa from 'papaparse';
import Cookies from 'js-cookie';

const { Option } = Select;
const { Text, Link } = Typography;

const HabitatComponent: React.FC = () => {
  const [terrestrialCommunityList, setTerrestrialCommunityList] = useState<
    { code: string; relationship: string; annexCode?: string }[]
  >([]);
  const [terrestrialRedList, setTerrestrialRedList] = useState<{ code: string; relationship: string }[]>([]);
  const [marineRedList, setMarineRedList] = useState<{ code: string; relationship: string }[]>([]);
  const [marineCommunityList, setMarineCommunityList] = useState<
    { code: string; relationship: string; annexCode?: string }[]
  >([]);

  const [habitatList, setHabitatList] = useState<Habitat[]>([]);
  const [selectedHabitat, setSelectedHabitat] = useState<Habitat | null>(null);

  const [terrestrialCommunityStatus, setTerrestrialCommunityStatus] = useState<{
    exists: boolean;
    relationship: string;
    annexCode?: string;
  }>({ exists: false, relationship: 'N/A' });
  const [terrestrialRedStatus, setTerrestrialRedStatus] = useState<{
    exists: boolean;
    relationship: string;
    annexCode?: string;
  }>({
    exists: false,
    relationship: 'N/A',
  });
  const [marineRedStatus, setMarineRedStatus] = useState<{ exists: boolean; relationship: string }>({
    exists: false,
    relationship: 'N/A',
  });
  const [marineCommunityStatus, setMarineCommunityStatus] = useState<{
    exists: boolean;
    relationship: string;
    annexCode?: string;
  }>({
    exists: false,
    relationship: 'N/A',
  });

  const relationshipTerrestrialMapping: Record<string, string> = {
    '=': 'The revised EUNIS habitat is equal to the Red List habitat',
    'â‰ˆ': 'The revised EUNIS habitat is more or less equal to the Red List habitat',
    '≈': 'The revised EUNIS habitat is more or less equal to the Red List habitat (treated as equal in EUNIS web)',
    '#': 'The revised EUNIS habitat overlaps with the Red List habitat',
    '>': 'The revised EUNIS habitat is wider than the Red List habitat',
    '<': 'The revised EUNIS habitat is narrower than the Red List habitat',
    'NA': 'The revised EUNIS habitat is not assessed in the Red List project (assigned only for level 3)',
    '': 'The revised EUNIS habitat is not linked to an Annex I type or is outside EU. No links are given to levels 1 and 2 as these are too numerous',
  };
  const relationshipTerrestrialCommunityMapping: Record<string, string> = {
    '=': 'The revised EUNIS habitat is equal to the habitats in Annex I of the Habitats Directive',
    'â‰ˆ': 'The revised EUNIS habitat is more or less equal to the habitats in Annex I of the Habitats Directive',
    '≈': 'The revised EUNIS habitat is more or less equal to the habitats in Annex I of the Habitats Directive',
    '#': 'The revised EUNIS habitat overlaps with the habitats in Annex I of the Habitats Directive',
    '>': 'The revised EUNIS habitat is wider than the habitats in Annex I of the Habitats Directive',
    '<': 'The revised EUNIS habitat is narrower than the habitats in Annex I of the Habitats Directive',
    'NA': 'The revised EUNIS habitat is not assessed in the habitats in Annex I of the Habitats Directive',
    '': 'The revised EUNIS habitat is not linked to an Annex I type or is outside EU. No links are given to levels 1 and 2 as these are too numerous',
  };
  const relationshipMarineMapping: Record<string, string> = {
    '=': 'The revised EUNIS habitat is equal to the Red List habitat',
    'â‰ˆ': 'The revised EUNIS habitat is more or less equal to the Red List habitat',
    '#': 'The revised EUNIS habitat overlaps with the Red List habitat',
    '>': 'The revised EUNIS habitat is wider than the Red List habitat',
    '<': 'The revised EUNIS habitat is narrower than the Red List habitat',
    '': 'The revised EUNIS habitat is not assessed in the Red List project',
  };
  const relationshipMarineCommunityMapping: Record<string, string> = {
    '=': 'The revised EUNIS habitat is equal to the Annex I of the Habitats Directive',
    '#': 'The revised EUNIS habitat overlaps with the Annex I of the Habitats Directive',
    '>': 'The revised EUNIS habitat is wider than the Annex I of the Habitats Directive',
    '<': 'The revised EUNIS habitat is narrower than the Annex I of the Habitats Directive',
    'NA': 'The revised EUNIS habitat is not assessed in the Annex I of the Habitats Directive',
    '': 'The revised EUNIS habitat is not linked to an Annex I type or is outside EU. No links are given to levels 1 and 2 as these are too numerous',
  };
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCommunityModalVisible, setIsCommunityModalVisible] = useState(false);

  const caseStudy = Cookies.get('caseStudy');
  const processCSV = async (
    file: string,
    codeColumn: string,
    relationshipColumn: string,
    relationshipWithCodeColumn?: string,
    annexColumn?: string,
    relationshipMapping?: Record<string, string>,
  ) => {
    return new Promise<{ code: string; relationship: string; annexCode?: string }[]>((resolve) => {
      Papa.parse(file, {
        download: true,
        header: true,
        complete: (result) => {
          // Use a Map to aggregate data by code
          let aggregatedData = new Map<string, {
            relationships: Set<string>;
            annexCodes: Set<string>;
          }>();

          result.data.forEach((row: any) => {
            const code = row[codeColumn] || 'N/A';
            let originalRelationship = row[relationshipColumn] || ''; // Use empty string for better handling of '' in mapping

            // Convert relationship characters to full descriptions
            const relationshipParts = originalRelationship
              .split('/') // Split in case of multiple symbols (e.g., ">/>")
              .map((char: string) => relationshipMapping?.[char.trim()]) // Trim to handle potential spaces
              .filter(Boolean); // Remove undefined/null values

            let mappedRelationship = relationshipParts.join(' / '); // Join them back

            // Append the value from relationshipWithCodeColumn if provided
            if (relationshipWithCodeColumn && row[relationshipWithCodeColumn]) {
              const codeParts = String(row[relationshipWithCodeColumn]).split('/');

              if (relationshipParts.length > 1 && codeParts.length > 1 && relationshipParts.length === codeParts.length) {
                // If both relationship and code have multiple parts, interleave them
                mappedRelationship = relationshipParts.map((relPart: string, index: number) => {
                  return `${relPart} ${codeParts[index as number]}`;
                }).join('/');
              } else if (mappedRelationship) {
                // If there's an existing mappedRelationship, add a space before appending
                mappedRelationship += ` ${row[relationshipWithCodeColumn]}`;
              } else {
                // If no mappedRelationship, just use the raw code
                mappedRelationship = String(row[relationshipWithCodeColumn]);
              }
            }

            // Get annex code and filter out 'N/A' or 'x'
            const currentAnnexCode = annexColumn ? (String(row[annexColumn]) || '').trim() : '';

            if (!aggregatedData.has(code)) {
              aggregatedData.set(code, {
                relationships: new Set(),
                annexCodes: new Set(),
              });
            }

            const entry = aggregatedData.get(code)!;
            if (mappedRelationship && mappedRelationship !== 'N/A') {
              entry.relationships.add(mappedRelationship);
            }
            // Add annex code only if it's a valid code (not empty, 'N/A', or 'x')
            if (currentAnnexCode && currentAnnexCode !== 'N/A' && currentAnnexCode.toLowerCase() !== 'x') {
              entry.annexCodes.add(currentAnnexCode);
            }
          });

          // Convert the aggregated Map back into an array of formatted objects
          const finalFormattedData = Array.from(aggregatedData.entries()).map(([code, data]) => {
            const uniqueRelationships = Array.from(data.relationships).join(', ');
            const uniqueAnnexCodes = Array.from(data.annexCodes).join(', ');

            return {
              code: code,
              relationship: uniqueRelationships || 'N/A',
              annexCode: uniqueAnnexCodes || undefined, // Return undefined if no valid annex codes were found
            };
          });
          resolve(finalFormattedData);
        },
      });
    });
  };

  const processJSONForDescriptions = async (habitats: Habitat[]): Promise<Habitat[]> => {
    try {
      // Create lookup maps by Code for all fields
      const terrestrialMap = new Map<
        string,
        {
          description: string;
          source: string;
          diagnosticSpecies: string;
          constantSpecies: string;
          dominantSpecies: string;
        }
      >();

      habitatTerrestrialDetailList.forEach((row) => {
        if (row.Code) {
          terrestrialMap.set(row.Code, {
            description: row.Description || '',
            source: row.Source || '',
            diagnosticSpecies: row['Diagnostic species'] || '',
            constantSpecies: row['Constant species'] || '',
            dominantSpecies: row['Dominant species'] || '',
          });
        }
      });

      const marineMap = new Map<
        string,
        {
          description: string;
          source: string;
          diagnosticSpecies: string;
          constantSpecies: string;
          dominantSpecies: string;
        }
      >();

      habitatMarineDetailList.forEach((row) => {
        if (row.Code) {
          marineMap.set(row.Code, {
            description: row.Description || '',
            source: row.Source || '',
            diagnosticSpecies: (row as any)['Diagnostic species'] || '',
            constantSpecies: (row as any)['Constant species'] || '',
            dominantSpecies: (row as any)['Dominant species'] || '',
          });
        }
      });

      // Update habitats with all available fields
      const updatedHabitats = habitats.map((habitat) => {
        let updatedFields = {
          description: '',
          source: '',
          diagnosticSpecies: '',
          constantSpecies: '',
          dominantSpecies: '',
        };

        // Check terrestrial first
        if (terrestrialMap.has(habitat.code)) {
          updatedFields = terrestrialMap.get(habitat.code)!;
        }

        // Check marine second (overwrites if code exists in both)
        if (marineMap.has(habitat.code)) {
          updatedFields = marineMap.get(habitat.code)!;
        }

        return {
          ...habitat,
          description: updatedFields.description,
          source: updatedFields.source,
          diagnosticSpecies: updatedFields.diagnosticSpecies,
          constantSpecies: updatedFields.constantSpecies,
          dominantSpecies: updatedFields.dominantSpecies,
        };
      });

      return updatedHabitats;
    } catch (error) {
      console.error('Error processing JSON files:', error);
      return habitats; // Return unchanged habitats on error
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (caseStudy) {
          const apiData = await fetchHabitats(caseStudy);
          const updatedHabitatsWithCsvDescriptions = await processJSONForDescriptions(apiData);
          setHabitatList(Array.isArray(updatedHabitatsWithCsvDescriptions) ? updatedHabitatsWithCsvDescriptions : []);
        }
        if (caseStudy === 'pnr_narbonnaise') {
          const terrestrialRed = await processCSV(
            habitatTerrestrialRedList,
            'Code',
            'Red List relationship',
            'Red List code',
            undefined,
            relationshipTerrestrialMapping,
          );
          const terrestrialCommunity = await processCSV(
            habitatTerrestrialCommunityList,
            'revised_EUNIS_Code',
            'relationship_EUNIS_to_Annex_I',
            'Annex_I_code',
            'Annex_I_code',
            relationshipTerrestrialCommunityMapping,
          );
          const marineRed = await processCSV(
            habitatMarineRedList,
            'Code',
            'Red List relationship',
            'Red List code',
            undefined,
            relationshipMarineMapping,
          );
          const marineCommunity = await processCSV(
            habitatMarineCommunityList,
            'Code 2019',
            'Relationship to Annex I',
            'Annex I code',
            'Annex I code',
            relationshipMarineCommunityMapping,
          );

          setTerrestrialCommunityList(terrestrialCommunity);
          setTerrestrialRedList(terrestrialRed);
          setMarineRedList(marineRed);
          setMarineCommunityList(marineCommunity);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    fetchData();
  }, []);

  const handleHabitatChange = (value: string) => {
    const selected = habitatList?.find((habitat) => habitat.code === value) || null;
    setSelectedHabitat(selected);
    if (selected && caseStudy === 'pnr_narbonnaise') {
      const terrestrialCommunityFound = terrestrialCommunityList.find((item) => item.code === selected.code);
      const terrestrialRedFound = terrestrialRedList.find((item) => item.code === selected.code);
      const marineRedFound = marineRedList.find((item) => item.code === selected.code);
      const marineCommunityFound = marineCommunityList.find((item) => item.code === selected.code);
      setTerrestrialCommunityStatus({
        exists: !!terrestrialCommunityFound,
        relationship: terrestrialCommunityFound?.relationship ?? 'N/A',
        annexCode: terrestrialCommunityFound?.annexCode ?? 'N/A',
      });

      setTerrestrialRedStatus({
        exists: !!terrestrialRedFound,
        relationship: terrestrialRedFound?.relationship ?? 'N/A',
      });

      setMarineRedStatus({
        exists: !!marineRedFound,
        relationship: marineRedFound?.relationship ?? 'N/A',
      });

      setMarineCommunityStatus({
        exists: !!marineCommunityFound,
        relationship: marineCommunityFound?.relationship ?? 'N/A',
        annexCode: marineCommunityFound?.annexCode ?? 'N/A',
      });
    }
  };
  const handleOk = () => {
    setIsModalVisible(false);
    setIsCommunityModalVisible(false);
  };
  const handleCancel = () => {
    setIsModalVisible(false); // Set modal visibility to false to close it
  };

  const renderDescriptionItem = (label: string, value: string | undefined | null) => {
    return value && value.trim() !== '' ? <Descriptions.Item label={label}>{value}</Descriptions.Item> : null;
  };

  return (
    <div>
      <h1 style={{ marginBottom: '20px', color: '#329619', fontSize: '25px' }}>Home &gt; Predicted Habitats</h1>

      <Row>
        <Col span={24}>
          <Card title='Habitat Information' style={{ height: 'auto', overflowY: 'auto' }}>
            {/* Habitat Selection */}
            <Row>
              <div>
                <Text strong style={{ marginRight: '10px' }}>
                  Habitat:
                </Text>
                <Select
                  id='habitatSelectId'
                  placeholder='Select a habitat code and name'
                  style={{ width: 'auto' }}
                  onChange={handleHabitatChange}
                  value={selectedHabitat?.code ?? undefined}
                >
                  {habitatList?.map((habitat) => (
                    <Option key={habitat.id} value={habitat.code}>
                      {habitat.code} - {habitat.name}
                    </Option>
                  ))}
                </Select>
              </div>
              {selectedHabitat && (
                <Descriptions style={{ marginTop: '10px', width: '100%' }} bordered column={1}>
                  {selectedHabitat.level && (
                    <Descriptions.Item label='Level'>{selectedHabitat.level}</Descriptions.Item>
                  )}
                  {renderDescriptionItem('Description', selectedHabitat.description)}
                  {renderDescriptionItem('Source', selectedHabitat.source)}
                  {renderDescriptionItem('Diagnostic Species', selectedHabitat.diagnosticSpecies)}
                  {renderDescriptionItem('Constant Species', selectedHabitat.constantSpecies)}
                  {renderDescriptionItem('Dominant Species', selectedHabitat.dominantSpecies)}
                </Descriptions>
              )}
            </Row>
            {caseStudy === 'pnr_narbonnaise' && (
              <>
                {/* CSV Data Overview */}
                <Row gutter={[16, 16]} id='habitatFranceCsvDataId' style={{ marginTop: '16px' }}>
                  {/* Terrestrial Red List */}
                  <Col xs={24} sm={12}>
                    <Card
                      size='small'
                      title={
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1890ff' }}
                          onClick={() => setIsModalVisible(true)}
                        >
                          EUNIS Terrestrial Habitat - European Red List Of Habitats
                        </span>
                      }
                    >
                      <p>
                        <Text strong>Status:</Text> {terrestrialRedStatus.exists ? '✅' : '❌'}
                      </p>
                      <p>
                        <Text strong>Relationship:</Text> {terrestrialRedStatus.relationship}
                      </p>
                    </Card>
                  </Col>
                  {/* Terrestrial Community */}
                  <Col xs={24} sm={12}>
                    <Card
                      size='small'
                      title={
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1890ff' }}
                          onClick={() => setIsCommunityModalVisible(true)}
                        >
                          EUNIS Terrestrial Habitats - Habitats In Annex I Of The Habitats Directive
                        </span>
                      }
                      variant='outlined'
                    >
                      <p>
                        <Text strong>Status:</Text> {terrestrialCommunityStatus.exists ? '✅' : '❌'}
                      </p>
                      <p>
                        <Text strong>Relationship:</Text> {terrestrialCommunityStatus.relationship}
                      </p>
                      <p>
                        <Text strong>Annex I Code:</Text> {terrestrialCommunityStatus.annexCode ?? 'N/A'}
                      </p>
                    </Card>
                  </Col>
                  {/* Marine Red List */}
                  <Col xs={24} sm={12}>
                    <Card
                      size='small'
                      title={
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1890ff' }}
                          onClick={() => setIsModalVisible(true)}
                        >
                          EUNIS Marine Habitat - European Red List Of Habitats
                        </span>
                      }
                      variant='outlined'
                    >
                      <p>
                        <Text strong>Status:</Text> {marineRedStatus.exists ? '✅' : '❌'}
                      </p>
                      <p>
                        <Text strong>Relationship:</Text> {marineRedStatus.relationship}
                      </p>
                    </Card>
                  </Col>
                  {/* Marine Community */}
                  <Col xs={24} sm={12}>
                    <Card
                      size='small'
                      title={
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1890ff' }}
                          onClick={() => setIsCommunityModalVisible(true)}
                        >
                          EUNIS Marine Habitat - Habitats In Annex I Of The Habitats Directive
                        </span>
                      }
                      variant='outlined'
                    >
                      <p>
                        <Text strong>Status:</Text> {marineCommunityStatus.exists ? '✅' : '❌'}
                      </p>
                      <p>
                        <Text strong>Relationship:</Text> {marineCommunityStatus.relationship}
                      </p>
                      <p>
                        <Text strong>Annex I Code:</Text> {marineCommunityStatus.annexCode ?? 'N/A'}
                      </p>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Card>
          {/* New Alert with GeoPl@ntNet link */}
          <Alert
            type='info'
            showIcon
            style={{ marginTop: '16px' }}
            message={
              <>
                <Text strong>Data Source:</Text> GeoPl@ntNet
                <br />
                <Link href='https://guarden.org/technologies/geoplantnet/' target='_blank'>
                  Learn more about GeoPl@ntNet
                </Link>
              </>
            }
          />
        </Col>
        <Col span={24} style={{ paddingTop: '5px' }}>
          <Card>
            <HabitatsMap selectedHabitat={selectedHabitat} />
          </Card>
        </Col>
      </Row>
      <Modal
        title='The Habitats Directive'
        open={isCommunityModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key='submit' type='primary' onClick={handleOk}>
            OK
          </Button>,
        ]}
      >
        <p>
          The habitats directive aims to protect species and habitats. The habitats listed in Annex I of this directive
          are referred to as Habitats of Community Interest (HCI). There are manuals for interpreting habitats, at both
          European and French levels. Annex I habitats may justify the creation of a Special Area of Conservation.
        </p>
        <p>
          Special area of conservation means « a site of Community importance designated by the Member States through a
          statutory, administrative and/or contractual act where the necessary conservation measures are applied for the
          maintenance or restoration, at a favourable conservation status, of the natural habitats and/or the
          populations of the species for which the site is designated » (see Council Directive 92/43/EEC).
        </p>
        <p>
          <strong>Extract from article 3 of the habitats directive:</strong>
          <br />« A coherent European ecological network of special areas of conservation shall be set up under the
          title Natura 2000. This network, composed of sites hosting the natural habitat types listed in Annex I and
          habitats of the species listed in Annex II, shall enable the natural habitat types and the species' habitats
          concerned to be maintained or, where appropriate, restored at a favourable conservation status in their
          natural range » (see Council Directive 92/43/EEC).
        </p>
        <p>
          <strong>Summary of article 6 paragraphs 1 to 4:</strong>
          <br />
          The Member States undertake, in particular, to:
          <br /> (1) establish conservation measures in Special Areas of Conservation that meet the ecological
          requirements of the natural habitat types listed in Annex I and the habitats of species listed in Annex II;
          <br /> (2) avoid the deterioration of natural habitats in Special Areas of Conservation;
          <br /> (3) approve a project on the site of a Special Area of Conservation if it does not adversely affect the
          conservation objectives of the site;
          <br /> (4) implement a compensatory measure if there are no alternative solutions to the project and if the
          project must be carried out for reasons of major public interest.
        </p>
        <p>
          To find out more about Natura 2000, click{' '}
          <a
            href='https://www.natura2000.fr/natura-2000/qu-est-ce-que-natura-2000'
            target='_blank'
            rel='noopener noreferrer'
          >
            here
          </a>
          .
        </p>
      </Modal>
      <Modal
        title='European Red List of Habitats'
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key='submit' type='primary' onClick={handleOk}>
            OK
          </Button>,
        ]}
      >
        <p>
          The European Environment Agency (EEA) proposes correspondences between the habitats of the EUNIS 2021 typology
          and the habitats of the European Red List. This list was established according to the assessment of the risk
          of ecosystem collapse of the International Union for Conservation of Nature (IUCN).
        </p>
        <p>
          The IUCN uses the ecosystem type as the unit for assessing the risk of ecosystem collapse. Ecosystem type is
          defined as a complex of organisms associated with their physical environments within a spatial boundary.
        </p>
        <p>
          IUCN considers the following terms to be synonymous with ecosystem type: ecological communities, habitats,
          biotopes, and vegetation types.
        </p>
        <p>
          Similar to the Red List of species, the IUCN categories are as follows, in descending order of risk of
          collapse:
          <br />- Collapsed (CO) - Critically Endangered (CR) - Endangered (EN) - Vulnerable (VU) - Near Threatened (NT)
          - Least Concern (LC) - Data Deficient (DD) - Not Evaluated (NE)
        </p>
        <p>
          <strong>The risk assessment criteria are:</strong>
          <br />- Criterion A: Decline in distribution - Criterion B: Restricted distribution - Criterion C:
          Environmental degradation - Criterion D: Disruption of biotic processes and interactions - Criterion E:
          Quantitative estimates of risk of ecosystem collapse
        </p>
        <p>
          To find out more about the IUCN assessment of the risk of ecosystem collapse, click{' '}
          <a
            href='https://iucnrle.org/documents_and_publications?page=1&category=rle_guidelines'
            target='_blank'
            rel='noopener noreferrer'
          >
            here
          </a>
          .
        </p>
      </Modal>
    </div>
  );
};

export default HabitatComponent;
